import React from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  MapPin,
  Clock,
  Mail,
  Facebook,
  Linkedin,
  Youtube,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import { SITE } from "@/data/site";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2H21l-6.52 7.45L22.5 22h-6.84l-4.78-6.27L5.2 22H2.44l7-8L1.5 2h6.99l4.32 5.71L18.244 2Zm-1.2 18h1.62L7.06 4H5.34l11.7 16Z" />
  </svg>
);
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.5 6.6a6.3 6.3 0 0 1-3.7-1.2V15a5.2 5.2 0 1 1-5.2-5.2c.3 0 .6 0 .9.1v2.5a2.7 2.7 0 1 0 1.8 2.6V2h2.4a3.9 3.9 0 0 0 3.9 3.9v.7Z" />
  </svg>
);

const Contact = () => {
  const { t } = useLang();

  const hourLabel = (k) => {
    if (k === "monFri") return t("common.monFri");
    if (k === "sat") return t("common.sat");
    if (k === "sun") return t("common.sun");
    return k;
  };
  const hourTime = (h) =>
    h.time === "closedOnlineOnly" ? t("common.closedOnlineOnly") : h.time;

  return (
    <div data-testid="page-contact">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
              {t("contact.eyebrow")}
            </span>
            <h1 className="mt-4 font-display font-semibold text-[#111111] text-[40px] sm:text-[54px] lg:text-[64px] leading-[1.05] tracking-[-0.03em]">
              {t("contact.title")}
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[17px] leading-relaxed text-[#4B5563]">
              {t("contact.lead")}
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* SIDEBAR */}
          <aside className="lg:col-span-4 space-y-6" data-testid="contact-sidebar">
            <div className="rounded-3xl bg-[#082133] text-[#FAFAFA] p-8 sm:p-10 relative overflow-hidden grain">
              <div className="relative">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#FAFAFA]/60 font-semibold">
                  {t("common.reachUs")}
                </span>
                <a
                  href={SITE.phoneHref}
                  data-testid="contact-phone-large"
                  className="mt-3 block font-display text-3xl sm:text-4xl font-semibold tracking-tight hover:opacity-90"
                >
                  {SITE.phone}
                </a>
                <p className="mt-4 text-[14px] text-[#FAFAFA]/70 leading-relaxed">
                  {t("common.anyTime")}
                </p>

                <ul className="mt-7 space-y-3 text-[14px]">
                  <li className="flex items-start gap-3">
                    <Mail className="h-4 w-4 mt-1 text-[#FAFAFA]/65" />
                    <a
                      href={`mailto:${SITE.email}`}
                      className="text-[#FAFAFA]/90 hover:text-[#FAFAFA]"
                      data-testid="contact-email"
                    >
                      {SITE.email}
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="rounded-3xl bg-white border border-[#E5E7EB] p-7 sm:p-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-[#DCE3DE] text-[#082133]">
                  <Clock className="h-4 w-4" />
                </span>
                <h3 className="font-display text-lg font-semibold text-[#111111]">
                  {t("common.operatingHours")}
                </h3>
              </div>
              <ul className="space-y-3 text-[14px]">
                {SITE.hours.map((h, i) => (
                  <li
                    key={i}
                    className="flex items-baseline justify-between gap-3"
                  >
                    <span className="text-[#4B5563]">{hourLabel(h.dayKey)}</span>
                    <span className="text-[#111111] font-medium text-right">
                      {hourTime(h)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl bg-white border border-[#E5E7EB] p-7 sm:p-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-[#DCE3DE] text-[#082133]">
                  <MapPin className="h-4 w-4" />
                </span>
                <h3 className="font-display text-lg font-semibold text-[#111111]">
                  {t("common.serviceAreas")}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {SITE.serviceAreas.map((a) => (
                  <span
                    key={a}
                    className="text-[12px] rounded-full bg-[#FAFAFA] border border-[#E5E7EB] px-3 py-1.5 text-[#111111]"
                  >
                    {a}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-[13px] text-[#4B5563]">
                {t("common.seeAllAreas")}.
              </p>
            </div>

            <div className="rounded-3xl bg-white border border-[#E5E7EB] p-7 sm:p-8">
              <h3 className="font-display text-lg font-semibold text-[#111111] mb-5">
                {t("common.followUs")}
              </h3>
              <div className="flex items-center gap-2.5 flex-wrap">
                <a href={SITE.social.facebook} target="_blank" rel="noreferrer" data-testid="contact-social-facebook" className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-[#FAFAFA] border border-[#E5E7EB] text-[#082133] hover:bg-[#082133] hover:text-[#FAFAFA] transition">
                  <Facebook className="h-4 w-4" />
                </a>
                <a href={SITE.social.linkedin} target="_blank" rel="noreferrer" data-testid="contact-social-linkedin" className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-[#FAFAFA] border border-[#E5E7EB] text-[#082133] hover:bg-[#082133] hover:text-[#FAFAFA] transition">
                  <Linkedin className="h-4 w-4" />
                </a>
                <a href={SITE.social.twitter} target="_blank" rel="noreferrer" data-testid="contact-social-x" className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-[#FAFAFA] border border-[#E5E7EB] text-[#082133] hover:bg-[#082133] hover:text-[#FAFAFA] transition">
                  <XIcon className="h-3.5 w-3.5" />
                </a>
                <a href={SITE.social.tiktok} target="_blank" rel="noreferrer" data-testid="contact-social-tiktok" className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-[#FAFAFA] border border-[#E5E7EB] text-[#082133] hover:bg-[#082133] hover:text-[#FAFAFA] transition">
                  <TikTokIcon className="h-4 w-4" />
                </a>
                <a href={SITE.social.youtube} target="_blank" rel="noreferrer" data-testid="contact-social-youtube" className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-[#FAFAFA] border border-[#E5E7EB] text-[#082133] hover:bg-[#082133] hover:text-[#FAFAFA] transition">
                  <Youtube className="h-4 w-4" />
                </a>
              </div>
            </div>
          </aside>

          {/* FORM CONTAINER */}
          <div className="lg:col-span-8">
            <div className="rounded-[32px] bg-white border border-[#E5E7EB] p-6 sm:p-10 lg:p-12 shadow-[0_30px_60px_-30px_rgba(8,33,51,0.15)]">
              <div className="flex items-start justify-between gap-6 flex-col sm:flex-row mb-8">
                <div>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
                    {t("contact.eyebrow")}
                  </span>
                  <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-[#111111] tracking-tight">
                    {t("contact.formTitle")}
                  </h2>
                  <p className="mt-2 text-[14px] text-[#4B5563] max-w-md">
                    {t("contact.formNote")}
                  </p>
                </div>
                <a
                  href={SITE.phoneHref}
                  data-testid="contact-form-call"
                  className="inline-flex items-center gap-2 text-[#082133] text-sm font-semibold hover:gap-3 transition-all"
                >
                  <Phone className="h-4 w-4" />
                  {SITE.phone}
                </a>
              </div>

              {/* GHL PLACEHOLDER */}
              <div
                data-testid="ghl-form-container"
                className="relative rounded-2xl overflow-hidden bg-[#FAFAFA] border border-dashed border-[#082133]/25 min-h-[560px] flex items-center justify-center"
              >
                {/* Paste your <iframe ... GHL form embed code below */}
                {/* <iframe src="https://YOUR-GHL-FORM-URL" style="width:100%;height:100%;border:none;" /> */}
                <div className="text-center px-6 py-12 max-w-md">
                  <div className="mx-auto h-14 w-14 rounded-2xl bg-[#082133] text-[#FAFAFA] inline-flex items-center justify-center">
                    <ArrowUpRight className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-[#111111]">
                    GoHighLevel form
                  </h3>
                  <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed">
                    {t("common.embedPlaceholder")}
                  </p>
                  <code className="mt-5 inline-block bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-[12px] text-[#082133]">
                    &lt;iframe src=&quot;GHL_URL&quot; /&gt;
                  </code>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <Link to="/services" className="flex-1">
                  <Button
                    variant="outline"
                    data-testid="contact-explore-services"
                    className="w-full rounded-full h-12 border-[#082133]/20 text-[#082133] hover:bg-[#082133]/5"
                  >
                    {t("common.ourServices")}
                  </Button>
                </Link>
                <a
                  href={SITE.phoneHref}
                  data-testid="contact-bottom-call"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full h-12 bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47] text-[14px] font-medium"
                >
                  <Phone className="h-4 w-4" />
                  {t("common.callNow")}: {SITE.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
